import * as R from 'ramda';

import { safeParseStorage } from '@atom/utilities/localStorageUtilities';
import {
  ASSIGNED_STATUS_ID,
  COMPLETED_STATUS_ID,
  IN_PROGRESS_STATUS_ID,
  IN_REVIEW_STATUS_ID,
  REQUESTED_STATUS_ID,
  UNASSIGNED_STATUS_ID,
} from '@atom/utilities/workOrderStatusUtilities';

import { MapLayersState } from './mapLayersReducer';

export const ATOM_MAP_LAYERS_KEY = 'atomMapLayers';
const LEGACY_MAP_LAYERS_KEY = 'layers';

export const DEFAULT_WORK_STATUS_IDS: ReadonlyArray<string> = [
  REQUESTED_STATUS_ID,
  UNASSIGNED_STATUS_ID,
  ASSIGNED_STATUS_ID,
  IN_PROGRESS_STATUS_ID,
  COMPLETED_STATUS_ID,
  IN_REVIEW_STATUS_ID,
];

export const DEFAULT_LAYERS_BASE = {
  assets: true,
  users: true,
  work: true,
  geospatialLayers: true,
  workStatusIds: new Set<string>(DEFAULT_WORK_STATUS_IDS),
  workTemplateIds: new Set<string>(),
  workTemplateFolderIds: new Set<string>(),
  kmlLayerIds: new Set<string>(),
  arcgisLayerIds: new Set<string>(),
  categoryIds: new Set<string>(),
};

// The stored layer state, plus whether this user has ever chosen which
// inventory layers to show.
export type InitialLayers = {
  layers: MapLayersState;
  firstRun: boolean;
};

const serializeLayers = R.evolve({
  assetSchemas: Array.from,
  workStatusIds: Array.from,
  workTemplateIds: Array.from,
  workTemplateFolderIds: Array.from,
  kmlLayerIds: Array.from,
  arcgisLayerIds: Array.from,
  categoryIds: Array.from,
});

const toStringSet = (value: unknown): Set<string> =>
  Array.isArray(value)
    ? new Set(value.filter((item): item is string => typeof item === 'string'))
    : new Set();

const toBool = (value: unknown, fallback: boolean): boolean =>
  typeof value === 'boolean' ? value : fallback;

// Coerce an arbitrary parsed object down to a valid MapLayersState, falling
// back field-by-field to defaults and dropping any unknown/stale fields.
// An id collection that is present but empty is kept empty — only a missing or
// malformed collection falls back to its default.
// clusterDensity is set to a placeholder here — callers always overwrite it
// with the current value from usePreferences (it's never sourced from storage).
const coerceLayers = (raw: Record<string, unknown>): MapLayersState => ({
  assets: toBool(raw.assets, DEFAULT_LAYERS_BASE.assets),
  users: toBool(raw.users, DEFAULT_LAYERS_BASE.users),
  work: toBool(raw.work, DEFAULT_LAYERS_BASE.work),
  geospatialLayers: toBool(
    raw.geospatialLayers ?? raw.kmlLayers,
    DEFAULT_LAYERS_BASE.geospatialLayers,
  ),
  assetSchemas: toStringSet(raw.assetSchemas),
  workStatusIds: Array.isArray(raw.workStatusIds)
    ? toStringSet(raw.workStatusIds)
    : new Set(DEFAULT_WORK_STATUS_IDS),
  workTemplateIds: toStringSet(raw.workTemplateIds),
  workTemplateFolderIds: toStringSet(raw.workTemplateFolderIds),
  kmlLayerIds: toStringSet(raw.kmlLayerIds),
  arcgisLayerIds: toStringSet(raw.arcgisLayerIds),
  categoryIds: toStringSet(raw.categoryIds),
  clusterDensity: 'default',
});

const fromRaw = (
  raw: unknown,
  clusterDensity: string,
): InitialLayers | null => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const blob = raw as Record<string, unknown>;

  return {
    layers: { ...coerceLayers(blob), clusterDensity },
    // An explicit list — empty or not — means the user has chosen. A missing
    // one means they never have, which is the only thing that licenses seeding.
    firstRun: !Array.isArray(blob.assetSchemas),
  };
};

export const readInitialLayers = (clusterDensity: string): InitialLayers => {
  const stored = fromRaw(safeParseStorage(ATOM_MAP_LAYERS_KEY), clusterDensity);

  if (stored) {
    return stored;
  }

  // Preferences written by the v1 map, for users who have not loaded the current map yet.
  const legacy = fromRaw(
    safeParseStorage(LEGACY_MAP_LAYERS_KEY),
    clusterDensity,
  );

  if (legacy) {
    return legacy;
  }

  // Nothing stored at all: no layers chosen yet, so the provider seeds every
  // schema once the list arrives.
  return {
    layers: {
      ...DEFAULT_LAYERS_BASE,
      assetSchemas: new Set<string>(),
      clusterDensity,
    },
    firstRun: true,
  };
};

export const writeAtomMapLayers = (layers: MapLayersState): void => {
  localStorage.setItem(
    ATOM_MAP_LAYERS_KEY,
    JSON.stringify(serializeLayers(layers)),
  );
};
